<template>
  <Container as="section" class="py-12 md:py-20">
    <Grid class="h-full lg:gap-20">
      <div class="flex flex-col col-start-1 col-end-13 lg:col-start-3 lg:col-end-11 max-w-[866px] gap-8 mx-auto">
        <!-- Header -->
        <div class="flex flex-col text-center gap-3 lg:gap-3.5">
          <Typography as="h2" variant="header-2">
            {{ displayTitle }}
          </Typography>
        </div>

        <!-- FAQ Accordion -->
        <div class="flex flex-col">
          <Accordion
            v-if="displayedFaqs.length > 0"
            :items="displayedFaqs"
            variant="light"
            class="faq-accordion">
            <template #item-button="{ button }">
              <div class="flex">
                <Button
                  v-if="toButton(button).variant !== 'text-link'"
                  :variant="mapButtonVariant(toButton(button).variant)"
                  size="md"
                  as="NuxtLink"
                  :to="toButton(button).url"
                  :external="toButton(button).openInNewTab">
                  {{ toButton(button).title }}
                </Button>
                <Link
                  v-else
                  variant="body"
                  underline
                  as="NuxtLink"
                  :to="toButton(button).url"
                  :target="toButton(button).openInNewTab ? '_blank' : undefined">
                  {{ toButton(button).title }}
                </Link>
              </div>
            </template>
          </Accordion>
        </div>

        <!-- Read More link -->
        <Link
          v-if="readMore"
          variant="body"
          class="self-center"
          has-icon
          as="NuxtLink"
          :to="readMore.url"
          :target="readMore.openInNewTab ? '_blank' : undefined">
          {{ readMore.title }}
          <template #icon>
            <ChevronRightIcon class="w-2.5 h-2.5" />
          </template>
        </Link>
      </div>
    </Grid>
  </Container>
</template>

<script setup lang="ts">
import type { CorporateButtonApiModel, CorporateFaqGridApiModel, FaqOverviewApiModel } from '@gro-up/api/corporate'
import type { UmbracoRichText } from '@gro-up/types'
import { Accordion, Button, Container, Typography, Grid, Link } from '@gro-up/ui'
import { ChevronRightIcon } from '@gro-up/ui/icons'
import { computed } from 'vue'

type FaqBlockProps = {
  header?: string | null
  title?: string
  faqGroupings?: FaqOverviewApiModel['faqGroupings']
  faqs?: CorporateFaqGridApiModel['faqs']
  readMore?: CorporateFaqGridApiModel['readMore']
}

const props = defineProps<FaqBlockProps>()

function toButton(button: unknown) {
  return button as CorporateButtonApiModel
}

const mapButtonVariant = (variant?: string): 'theme' | 'theme-inverse' | 'primary' | 'secondary' | 'top-nav' => {
  switch (variant) {
    case 'theme':
    case 'theme-inverse':
    case 'primary':
    case 'secondary':
    case 'top-nav':
      return variant
    default:
      return 'secondary'
  }
}

// Display title - supports both 'header' (from faqGrid) and 'title' (from faqBlock)
const displayTitle = computed(() => props.header || props.title || 'Veelgestelde vragen')

// Flatten all FAQs from all groupings for display
const displayedFaqs = computed(() => {
  const allFaqs: {
    question: string
    answer: string | UmbracoRichText
    button?: unknown
  }[] = []

  // Handle faqGrid structure (flat faqs array)
  if (props.faqs && props.faqs.length > 0) {
    props.faqs.forEach(faq => {
      allFaqs.push({
        question: faq.question,
        answer: faq.answer as string | UmbracoRichText,
        button: faq.button,
      })
    })
  }

  // Handle faqBlock structure (faqGroupings)
  if (props.faqGroupings && props.faqGroupings.length > 0) {
    props.faqGroupings.forEach(grouping => {
      grouping.faqs.forEach(faq => {
        allFaqs.push({
          question: faq.question,
          answer: faq.answer as string | UmbracoRichText,
          button: faq.button,
        })
      })
    })
  }

  return allFaqs
})
</script>

<style lang="scss" scoped>
.faq-accordion {
  :deep(.text-header-5) {
    font-size: 18px !important;
    line-height: 1.4;
  }
}
</style>

